import { deriveSurfaceScale } from './deriveSurfaceScale';
import { isDarkBackground } from './isDarkBackground';

/**
 * Master switch for tenant `secondary_color` theming — the app-wide background,
 * dark mode, and the derived surface scale.
 *
 * DISABLED for now: the portal renders in its default light/white look and
 * ignores the backend `secondary_color`; only `primary_color` (the accent) is
 * applied. Flip to `true` to bring the whole secondary-driven system back — both
 * consumers gate on it (Theme.store's `syncTenantTheme` call and Company.store's
 * `useIsTenantDark`), and all the machinery below stays intact.
 */
export const TENANT_BG_THEMING_ENABLED: boolean = false;

// The sidebar foreground group, set to white / translucent-white when the
// tenant background is dark. On a light background these are removed so the
// tokens.css defaults (dark text, light hover/border) apply.
const SIDEBAR_FG_ON_DARK: Record<string, string> = {
  '--wh-sidebar-fg': '#FFFFFF',
  '--wh-sidebar-fg-muted': 'rgba(255, 255, 255, 0.7)',
  '--wh-sidebar-hover': 'rgba(255, 255, 255, 0.12)',
  '--wh-sidebar-border': 'rgba(255, 255, 255, 0.24)',
  '--wh-sidebar-surface': 'rgba(255, 255, 255, 0.08)',
};

// The neutral surface tokens derived from secondary_color (see
// deriveSurfaceScale). Kept as a static list so an unset tenant clears exactly
// what a set tenant paints, falling back to the tokens.css static defaults.
const SURFACE_VARS = [
  '--bg-default',
  '--bg-surface',
  '--bg-hover',
  '--bg-active',
  '--bg-muted',
  '--border-default',
  '--border-strong',
  '--color-card',
  '--color-popover',
] as const;

/**
 * Apply the tenant's "Secondary color (Light)" as the app-wide background and,
 * when it is dark enough that white text clears WCAG AA, switch the app into
 * dark mode (adds the `dark-mode` class that flips the semantic tokens) and
 * flip the sidebar foreground group to white. It also derives the whole neutral
 * surface scale (base + elevated cards/hover/muted + borders) from the color so
 * every surface is one coherent tint of the brand background. Unset is a full
 * reset to the tokens.css static defaults.
 */
export const syncTenantTheme = (
  root: HTMLElement,
  secondaryColor: string | undefined
): void => {
  if (!secondaryColor) {
    root.style.removeProperty('--wh-app-bg');
    root.classList.remove('dark-mode');
    Object.keys(SIDEBAR_FG_ON_DARK).forEach((v) =>
      root.style.removeProperty(v)
    );
    SURFACE_VARS.forEach((v) => root.style.removeProperty(v));
    return;
  }

  root.style.setProperty('--wh-app-bg', secondaryColor);

  const dark = isDarkBackground(secondaryColor);
  root.classList.toggle('dark-mode', dark);

  Object.entries(SIDEBAR_FG_ON_DARK).forEach(([v, value]) => {
    if (dark) root.style.setProperty(v, value);
    else root.style.removeProperty(v);
  });

  // Paint the neutral surface scale derived from the secondary color. On an
  // unparseable color deriveSurfaceScale returns {}, so we clear the vars and
  // fall back to the tokens.css defaults instead of applying garbage.
  const scale = deriveSurfaceScale(secondaryColor);
  SURFACE_VARS.forEach((v) => {
    if (scale[v]) root.style.setProperty(v, scale[v]);
    else root.style.removeProperty(v);
  });
};
